// Thin client for the same-origin /api. Cookies ride along automatically.

import type {
  AdminPost,
  Block,
  Comment,
  EditablePost,
  FeedPost,
  Friend,
  FriendInput,
  LoveState,
  PostDetail,
  User,
} from "./types";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await fetch(path, {
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", ...init?.headers },
      ...init,
    });
  } catch {
    throw new ApiError(0, "Can't reach the farm right now — check your connection.");
  }

  const data = (await res.json().catch(() => null)) as unknown;
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data && typeof data.error === "string"
        ? data.error
        : null) ?? "Something went sideways. Give it another go.";
    throw new ApiError(res.status, message);
  }
  return data as T;
}

/** Upload a file via XHR so we can track progress. Returns the parsed JSON response. */
function uploadXhr<T>(
  method: string,
  path: string,
  body: FormData | File,
  onProgress: (pct: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, path);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText) as T;
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          const msg =
            (data && typeof data === "object" && "error" in data
              ? (data as Record<string, unknown>).error
              : null) ?? "Upload failed — give it another go.";
          reject(new ApiError(xhr.status, String(msg)));
        }
      } catch {
        reject(new ApiError(xhr.status, "Upload failed — give it another go."));
      }
    };
    xhr.onerror = () => reject(new ApiError(0, "Upload failed — check your connection."));
    xhr.send(body);
  });
}

export const api = {
  me: () => request<{ user: User | null }>("/api/auth/me"),
  login: (handle: string, password: string) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ handle, password }),
    }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  // Feed + reading
  feed: (offset = 0, limit = 20) =>
    request<{ posts: FeedPost[]; hasMore: boolean; unreadCount: number }>(
      `/api/posts?offset=${offset}&limit=${limit}`,
    ),
  post: (id: number) => request<{ post: PostDetail }>(`/api/posts/${id}`),

  // Loving (PRD §3.5) — POST to love, DELETE to unlove; both return the fresh state.
  love: (id: number) =>
    request<LoveState>(`/api/posts/${id}/love`, { method: "POST" }),
  unlove: (id: number) =>
    request<LoveState>(`/api/posts/${id}/love`, { method: "DELETE" }),

  // Comments (PRD §3.6)
  comments: (postId: number) =>
    request<{ comments: Comment[] }>(`/api/posts/${postId}/comments`),
  addComment: (postId: number, body: string) =>
    request<{ comment: Comment }>(`/api/posts/${postId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  editComment: (commentId: number, body: string) =>
    request<{ comment: Comment }>(`/api/comments/${commentId}`, {
      method: "PATCH",
      body: JSON.stringify({ body }),
    }),
  deleteComment: (commentId: number) =>
    request<{ ok: true }>(`/api/comments/${commentId}`, { method: "DELETE" }),

  // Own profile (PRD §3.12) — emoji + avatar only; credentials aren't self-editable.
  updateProfile: (patch: { emoji?: string; avatarUrl?: null }) =>
    request<{ user: User }>("/api/auth/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  uploadAvatar: (file: File, onProgress: (pct: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return uploadXhr<{ user: User }>("POST", "/api/auth/me/avatar", form, onProgress);
  },

  // Admin authoring
  adminPosts: (status: "all" | "published" | "draft" = "all", month?: string) =>
    request<{ posts: AdminPost[] }>(
      `/api/admin/posts?status=${status}${month ? `&month=${month}` : ""}`,
    ),
  adminPost: (id: number) => request<{ post: EditablePost }>(`/api/admin/posts/${id}`),
  createPost: (title: string, blocks: Block[]) =>
    request<{ post: AdminPost }>("/api/admin/posts", {
      method: "POST",
      body: JSON.stringify({ title, blocks }),
    }),
  updatePost: (id: number, patch: { title?: string; blocks?: Block[] }) =>
    request<{ post: EditablePost }>(`/api/admin/posts/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  publishPost: (id: number, mode: "now" | "original" = "now") =>
    request<{ post: EditablePost }>(`/api/admin/posts/${id}/publish`, {
      method: "POST",
      body: JSON.stringify({ mode }),
    }),
  unpublishPost: (id: number) =>
    request<{ post: EditablePost }>(`/api/admin/posts/${id}/unpublish`, { method: "POST" }),
  deletePost: (id: number) =>
    request<{ ok: true }>(`/api/admin/posts/${id}`, { method: "DELETE" }),

  // Media uploads
  uploadImage: (file: File, onProgress: (pct: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return uploadXhr<{ url: string; provider: string }>("POST", "/api/admin/media/image", form, onProgress);
  },
  // Short clips → R2 (played with a native <video>). No paid Stream needed.
  uploadVideo: (file: File, onProgress: (pct: number) => void) => {
    const form = new FormData();
    form.append("file", file);
    return uploadXhr<{ url: string; provider: string }>("POST", "/api/admin/media/video-file", form, onProgress);
  },

  // Admin — invite friends
  friends: () => request<{ friends: Friend[] }>("/api/admin/friends"),
  createFriend: (input: FriendInput) =>
    request<{ friend: Friend }>("/api/admin/friends", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateFriend: (id: number, patch: Partial<FriendInput>) =>
    request<{ friend: Friend }>(`/api/admin/friends/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),
  deleteFriend: (id: number) =>
    request<{ ok: true }>(`/api/admin/friends/${id}`, { method: "DELETE" }),
};
